/**
 * @file UserSignupAnswers.tsx
 * @brief Read-only view of the two post-signup questions, per user.
 *
 * @details
 * The app asks every new account where it heard about Verblizr and which
 * screen it wants to land on. Both answers have been stored on `users` since
 * 22-09-2026 and nothing showed them anywhere — this is the first surface.
 *
 * ⚠️ It fetches by id rather than reading the row it is handed. The Users LIST
 * endpoint names its columns and does not include these five; only
 * `GET /admin/users/:id` does, because that one selects `u.*`. A list row will
 * always have them undefined, so the detail query is not optional here.
 *
 * ⛔ `primary_use` is NOT "what they answered at signup". Profile → Main use
 * writes the same column, so it is the account's CURRENT main use (it sets
 * Home's slide button since 12-10-2026; before, the screen the app opened
 * on), and `primary_use_at` moves with it. `signup_source` is the clean
 * one: only the signup question ever writes it. The copy below says so,
 * because a reader who assumes otherwise will miscount.
 *
 * @dependencies
 * - features/users/api: `useUser`
 *
 * @references
 * - FRONTEND/src/features/auth/onboardingApi.ts — owns the codes and labels
 * - BACKEND/src/db/migrations/ensure_onboarding_answers.js — the columns
 *
 * @author DES
 * @date 23-09-2026
 */

import { SkeletonFields } from "@/components/ui/Skeleton";
import { useUser } from "../api";
import { formatDateTime } from "@/shared/utils/formatters";

/**
 * ⛔ Codes, never the rendered labels, are what the database holds — a copy
 * change in the app must not fork the data. These maps are this panel's copy
 * of the app's own lists (`SIGNUP_SOURCES`, `PRIMARY_USES`); an unknown code
 * falls through to the raw value rather than rendering blank, so a new option
 * shipped in the app shows up here as itself instead of disappearing.
 */
const SIGNUP_SOURCE_LABELS: Record<string, string> = {
  friend: "Friend or colleague",
  work: "Work or employer",
  search: "Search engine",
  app_store: "App Store",
  social: "Social media",
  video: "YouTube or TikTok",
  linkedin: "LinkedIn",
  ai_chat: "AI chat",
  newsletter: "Newsletter or blog",
  news: "News or press",
  event: "Event or conference",
  other: "Something else",
};

const PRIMARY_USE_LABELS: Record<string, string> = {
  meetings: "Meetings",
  interpretation: "Interpretation",
  notes: "Personal notes",
  medical: "Medical assessments",
  home: "A bit of everything",
};

function Answer({
  label,
  value,
  detail,
  at,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  detail?: string | null;
  at?: string | null;
  hint?: string;
}) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      {value ? (
        <>
          <p className="text-sm font-medium">{value}</p>
          {detail ? (
            <p className="text-sm text-muted-foreground">“{detail}”</p>
          ) : null}
          {at ? (
            <p className="text-[11px] text-muted-foreground">
              {formatDateTime(at)}
            </p>
          ) : null}
        </>
      ) : (
        /* ⚠️ "Not answered" covers both skipping the question and never having
           been asked — the two are indistinguishable in the data, and saying
           more than that would be inventing a fact. */
        <p className="text-sm text-muted-foreground">Not answered</p>
      )}
      {hint ? (
        <p className="text-[11px] text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function UserSignupAnswers({
  userId,
  enabled = true,
}: {
  userId: string;
  enabled?: boolean;
}) {
  const { data, isLoading, isError } = useUser(userId, enabled);

  if (isLoading) {
    return <SkeletonFields fields={3} />;
  }
  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground">
        Could not load this user’s signup answers.
      </p>
    );
  }

  const source = data.signup_source
    ? SIGNUP_SOURCE_LABELS[data.signup_source] ?? data.signup_source
    : null;
  const use = data.primary_use
    ? PRIMARY_USE_LABELS[data.primary_use] ?? data.primary_use
    : null;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Answer
        label="Heard about Verblizr"
        value={source}
        /* Free text exists for the "Something else" option only. */
        detail={data.signup_source_detail}
        at={data.signup_source_at}
      />
      <Answer
        label="Main use"
        value={use}
        at={data.primary_use_at}
        hint="Current preference — the user can change this in Profile."
      />
    </div>
  );
}

export default UserSignupAnswers;
